import { Injectable } from '@angular/core';

@Injectable({
    providedIn: 'root'
})
export class PwaDebugService {



    /**
     * Comprehensive PWA debugging method
     */
    debugPwaInstallability(): void {
        console.group('🔍 PWA Installability Debug Report');

        try {
            // Basic environment checks
            this.checkEnvironment();

            // Manifest checks
            this.checkManifest();

            // Service Worker checks
            this.checkServiceWorker();

            // Icon checks
            this.checkIcons();

            // Event listener checks
            this.checkEventListeners();

        } catch (error) {
            console.error('Error during PWA debug:', error);
        } finally {
            console.groupEnd();
        }
    }

    private checkEnvironment(): void {
        console.group('🌐 Environment Checks');
        console.log('✓ Protocol:', window.location.protocol);
        console.log('✓ Hostname:', window.location.hostname);
        console.log('✓ Is Secure Context:', window.isSecureContext);
        console.log('✓ User Agent:', navigator.userAgent);
        console.log('✓ Display Mode:', window.matchMedia('(display-mode: standalone)').matches ? 'Standalone' : 'Browser');
        console.log('✓ Viewport Width:', window.innerWidth);
        console.log('✓ Viewport Height:', window.innerHeight);
        console.groupEnd();
    }

    private checkManifest(): void {
        console.group('📋 Manifest Checks');

        const manifestLink = document.querySelector('link[rel="manifest"]') as HTMLLinkElement;
        if (manifestLink) {
            console.log('✓ Manifest link found:', manifestLink.href);

            // Try to fetch manifest
            fetch(manifestLink.href)
                .then(response => {
                    if (!response.ok) {
                        console.error('❌ Manifest fetch failed:', response.status, response.statusText);
                    } else {
                        console.log('✓ Manifest accessible');
                        return response.json();
                    }
                })
                .then(manifest => {
                    if (manifest) {
                        console.log('✓ Manifest content:', manifest);
                        this.validateManifestContent(manifest);
                    }
                })
                .catch(error => {
                    console.error('❌ Manifest fetch error:', error);
                });
        } else {
            console.error('❌ No manifest link found');
        }

        console.groupEnd();
    }

    private validateManifestContent(manifest): void {
        console.group('📋 Manifest Content Validation');

        // Check required fields
        const requiredFields = ['name', 'short_name', 'start_url', 'display', 'icons'];
        requiredFields.forEach(field => {
            if (manifest[field]) {
                console.log(`✓ ${field}:`, manifest[field]);
            } else {
                console.error(`❌ Missing required field: ${field}`);
            }
        });

        // Check icons
        if (manifest.icons && Array.isArray(manifest.icons)) {
            console.log('✓ Icons found:', manifest.icons.length);

            const requiredSizes = ['192x192', '512x512'];
            const availableSizes = manifest.icons.map((icon) => icon.sizes);

            requiredSizes.forEach(size => {
                if (availableSizes.includes(size)) {
                    console.log(`✓ Required icon size ${size} found`);
                } else {
                    console.error(`❌ Missing required icon size: ${size}`);
                }
            });

            // Check for maskable icons
            const maskableIcons = manifest.icons.filter((icon) =>
                icon.purpose && icon.purpose.includes('maskable')
            );

            if (maskableIcons.length > 0) {
                console.log('✓ Maskable icons found:', maskableIcons.length);
            } else {
                console.warn('⚠️ No maskable icons found');
            }
        } else {
            console.error('❌ No icons array found in manifest');
        }

        console.groupEnd();
    }

    private checkServiceWorker(): void {
        console.group('⚙️ Service Worker Checks');

        if ('serviceWorker' in navigator) {
            console.log('✓ Service Worker supported');

            navigator.serviceWorker.getRegistrations().then(registrations => {
                console.log('✓ Service Worker registrations:', registrations.length);

                if (registrations.length > 0) {
                    registrations.forEach((reg, index) => {
                        console.log(`✓ Registration ${index + 1}:`, {
                            scope: reg.scope,
                            active: !!reg.active,
                            installing: !!reg.installing,
                            waiting: !!reg.waiting
                        });
                    });
                } else {
                    console.error('❌ No service worker registrations found');
                }
            }).catch(error => {
                console.error('❌ Error checking service worker registrations:', error);
            });
        } else {
            console.error('❌ Service Worker not supported');
        }

        console.groupEnd();
    }

    private checkIcons(): void {
        console.group('🖼️ Icon Accessibility Checks');

        const iconSizes = ['72x72', '96x96', '128x128', '144x144', '152x152', '192x192', '384x384', '512x512'];

        iconSizes.forEach(size => {
            const iconUrl = `assets/icons/icon-${size}.png`;

            // Create a test image to check if icon loads
            const img = new Image();
            img.onload = () => {
                console.log(`✓ Icon ${size} accessible`);
            };
            img.onerror = () => {
                console.error(`❌ Icon ${size} not accessible:`, iconUrl);
            };
            img.src = iconUrl;
        });

        console.groupEnd();
    }

    private checkEventListeners(): void {
        console.group('🎧 Event Listener Checks');

        console.log('✓ beforeinstallprompt supported:', 'onbeforeinstallprompt' in window);
        console.log('✓ appinstalled supported:', 'onappinstalled' in window);

        // Test beforeinstallprompt event firing (it won't fire if already handled)
        const testHandler = (e: Event) => {
            console.log('✓ beforeinstallprompt event test fired');
            e.preventDefault();
            window.removeEventListener('beforeinstallprompt', testHandler);
        };

        window.addEventListener('beforeinstallprompt', testHandler);

        // Remove test handler after 5 seconds
        setTimeout(() => {
            window.removeEventListener('beforeinstallprompt', testHandler);
        }, 5000);

        console.groupEnd();
    }

    /**
     * Force trigger PWA install criteria check
     */
    checkInstallCriteria(): void {
        console.group('📋 PWA Install Criteria Check');

        const criteria = {
            'HTTPS/Secure Context': window.isSecureContext,
            'Service Worker Registered': false,
            'Manifest with Required Fields': false,
            'Proper Icons (192x192 & 512x512)': false,
            'Not Already Installed': !window.matchMedia('(display-mode: standalone)').matches,
            'Not Dismissed Recently': true, // Can't check this programmatically
            'User Engagement': true // Can't check this programmatically
        };

        // Check service worker
        if ('serviceWorker' in navigator) {
            navigator.serviceWorker.getRegistrations().then(registrations => {
                criteria['Service Worker Registered'] = registrations.length > 0;
                this.displayCriteria(criteria);
            });
        } else {
            this.displayCriteria(criteria);
        }

        console.groupEnd();
    }

    private displayCriteria(criteria: any): void {
        Object.entries(criteria).forEach(([key, value]) => {
            if (value) {
                console.log(`✓ ${key}: PASS`);
            } else {
                console.error(`❌ ${key}: FAIL`);
            }
        });
    }
}
